import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  OrderService,
  OrderResponse,
} from 'src/app/services/order/order.service';

export interface CancelReason {
  key: string;
  labelKey: string;
}

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.css'],
})
export class OrderDetailsComponent implements OnInit {
  order: OrderResponse | null = null;
  isLoading = true;
  orderId: string | null = null;

  // Cancellation sheet
  showCancelSheet = false;
  selectedReason: string | null = null;
  customReason = '';
  isCancelling = false;

  cancelReasons: CancelReason[] = [
    { key: 'PRODUCT_PROBLEM', labelKey: 'ORDERS.CANCEL_REASON_PRODUCT' },
    { key: 'DELIVERY_PROBLEM', labelKey: 'ORDERS.CANCEL_REASON_DELIVERY' },
    { key: 'OTHER_PROBLEM', labelKey: 'ORDERS.CANCEL_REASON_OTHER' },
  ];

  // ✅ Added CONFIRMED to stepper
  statusSteps: { key: string }[] = [
    { key: 'PENDING' },
    { key: 'CONFIRMED' },
    { key: 'PROCESSING' },
    { key: 'SHIPPED' },
    { key: 'DELIVERED' },
  ];

  // ✅ CONFIRMED is still cancellable, only SHIPPED+ are not
  private readonly nonCancellableStatuses = [
    'SHIPPED',
    'DELIVERED',
    'CANCELLED',
  ];

  constructor(
    private orderService: OrderService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.orderId = this.route.snapshot.paramMap.get('id');
    if (this.orderId) this.loadOrder(this.orderId);
  }

  loadOrder(orderId: string): void {
    this.orderService.getUserOrder(orderId).subscribe({
      next: (res: any) => {
        this.order = res;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  isStatusCompleted(stepKey: string): boolean {
    if (!this.order) return false;
    const currentIndex = this.statusSteps.findIndex(
      (s) => s.key === this.order?.status,
    );
    const stepIndex = this.statusSteps.findIndex((s) => s.key === stepKey);
    return stepIndex < currentIndex;
  }

  isLineActive(index: number): boolean {
    const nextStep = this.statusSteps[index + 1];
    if (!nextStep) return false;
    return (
      this.isStatusCompleted(nextStep.key) ||
      this.order?.status === nextStep.key
    );
  }

  goBack(): void {
    this.router.navigate(['profile/myorders']);
  }

  get canCancelOrder(): boolean {
    if (!this.order) return false;
    return !this.nonCancellableStatuses.includes(this.order.status);
  }

  openCancelSheet(): void {
    if (!this.canCancelOrder) return;
    this.selectedReason = null;
    this.customReason = '';
    this.showCancelSheet = true;
  }

  closeCancelSheet(): void {
    this.showCancelSheet = false;
  }

  selectReason(key: string): void {
    this.selectedReason = key;
    if (key !== 'OTHER_PROBLEM') this.customReason = '';
  }

  get canSubmitCancel(): boolean {
    if (!this.selectedReason) return false;
    if (this.selectedReason === 'OTHER_PROBLEM')
      return this.customReason.trim().length > 0;
    return true;
  }

  submitCancel(): void {
    if (!this.order || !this.canSubmitCancel) return;

    this.isCancelling = true;

    const reason = this.selectedReason!;
    const note = this.customReason;

    this.orderService.cancelOrder(this.order.id, reason, note).subscribe({
      next: () => {
        this.isCancelling = false;
        this.showCancelSheet = false;
        if (this.order) this.order.status = 'CANCELLED';
      },
      error: (err) => {
        this.isCancelling = false;
      },
    });
  }

  contactSupport(): void {
    /* TODO */
  }

  get isRTL(): boolean {
    return document.documentElement.dir === 'rtl';
  }
}
